// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAccessToken, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Handles the URL fragment #session_id=... returned by Emergent Auth.
 * Exchanges the session_id for our own JWT via POST /api/auth/google/session,
 * then routes the user to /admin (if role=admin) or / (public listener).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : "";

    if (!sessionId) {
      setError("No session id found. Please try signing in again.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post(
          "/auth/google/session",
          {},
          { headers: { "X-Session-ID": sessionId } }
        );
        if (data.access_token) setAccessToken(data.access_token);
        // Clear the fragment before we route so a refresh doesn't retry.
        try { window.history.replaceState({}, document.title, window.location.pathname); } catch (_e) { void _e; }
        await refresh();
        if (data.role === "admin") {
          navigate("/admin", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (e) {
        const msg = formatApiError(e.response?.data?.detail) || e.message;
        setError(msg || "Sign-in failed. Please try again.");
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen bg-ink-900 text-white flex items-center justify-center px-6" data-testid="auth-callback-page">
      <div className="text-center">
        {error ? (
          <>
            <p className="text-neon-magenta text-sm mb-3" data-testid="auth-callback-error">{error}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="text-xs font-mono uppercase tracking-widest text-neon-cyan hover:underline"
              data-testid="auth-callback-back"
            >
              Back to sign-in
            </button>
          </>
        ) : (
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-zinc-400 animate-pulse">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
}
